/**
 * Local map pin storage (browser localStorage only).
 */

import { createPinId } from '../lib/uuid.js';
import { parseCoordPair } from './pin-import.js';

/**
 * @param {string} locId
 */
export function pinStorageKey(locId) {
    return `pota-gridscout-map-pins-${locId}`;
}

/**
 * @param {object} pin
 */
export function normalizePin(pin) {
    return {
        id: String(pin.id),
        lat: Number(pin.lat),
        lon: Number(pin.lon),
        note: String(pin.note || '').trim(),
        createdAt: pin.createdAt || new Date().toISOString(),
        updatedAt: pin.updatedAt || pin.createdAt || new Date().toISOString(),
    };
}

/**
 * @param {string} locId
 */
export class MapPinStore {
    constructor(locId) {
        this.locId = locId;
        /** @type {Map<string, object>} */
        this.pins = new Map();
    }

    loadFromLocalStorage() {
        if (typeof localStorage === 'undefined') {
            return;
        }

        try {
            const raw = localStorage.getItem(pinStorageKey(this.locId));
            if (!raw) {
                return;
            }
            const data = JSON.parse(raw);
            const rows = Array.isArray(data.pins) ? data.pins : [];
            for (const row of rows) {
                if (!row?.id) {
                    continue;
                }
                const pin = normalizePin(row);
                if (!Number.isFinite(pin.lat) || !Number.isFinite(pin.lon)) {
                    continue;
                }
                this.pins.set(pin.id, pin);
            }
        } catch {
            this.pins.clear();
        }
    }

    saveToLocalStorage() {
        if (typeof localStorage === 'undefined') {
            return;
        }

        const payload = {
            locId: this.locId,
            updatedAt: new Date().toISOString(),
            pins: this.getAll(),
        };
        localStorage.setItem(pinStorageKey(this.locId), JSON.stringify(payload));
    }

    /**
     * @param {number} lat
     * @param {number} lon
     * @param {string} [note]
     */
    add(lat, lon, note = '') {
        const coords = parseCoordPair(lat, lon);
        if (!coords) {
            return null;
        }

        const pin = normalizePin({
            id: createPinId(),
            lat: coords.lat,
            lon: coords.lon,
            note,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
        });
        this.pins.set(pin.id, pin);
        this.saveToLocalStorage();
        return pin;
    }

    /**
     * @param {{ lat: number, lon: number, note?: string }[]} rows
     */
    addMany(rows) {
        /** @type {object[]} */
        const added = [];
        for (const row of rows) {
            const coords = parseCoordPair(row.lat, row.lon);
            if (!coords) {
                continue;
            }
            const pin = normalizePin({
                id: createPinId(),
                lat: coords.lat,
                lon: coords.lon,
                note: row.note || '',
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
            });
            this.pins.set(pin.id, pin);
            added.push(pin);
        }
        if (added.length) {
            this.saveToLocalStorage();
        }
        return added;
    }

    /**
     * @param {string} id
     * @param {{ note?: string }} patch
     */
    update(id, patch) {
        const existing = this.pins.get(id);
        if (!existing) {
            return null;
        }

        const next = normalizePin({
            ...existing,
            ...patch,
            updatedAt: new Date().toISOString(),
        });
        this.pins.set(id, next);
        this.saveToLocalStorage();
        return next;
    }

    /**
     * @param {string} id
     */
    remove(id) {
        const removed = this.pins.delete(id);
        if (removed) {
            this.saveToLocalStorage();
        }
        return removed;
    }

    get(id) {
        return this.pins.get(id) || null;
    }

    getAll() {
        return [...this.pins.values()].sort((a, b) => String(b.updatedAt).localeCompare(String(a.updatedAt)));
    }
}
